import * as React from 'react';
// Components
import { FieldWrapper } from '@progress/kendo-react-form';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
import BaseDropDownTree from 'shared/inputs/BaseDropDownTree';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A dropdown tree form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<BaseDropDownTree>} BaseDropDownTreeProps
 *
 * @param {BaseFieldRenderProps & BaseDropDownTreeProps} fieldRenderProps
 * @returns {React.ReactElement} Dropdown tree form field.
 */
const FormDropDownTree = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    disabled,
    hint,
    wrapperStyle,
    inlinePopup = false,
    readOnly = false,
    optional,
    ...others
  } = fieldRenderProps;

  const { value } = others;

  const editorId = id ?? name;

  // Popup settings
  const inlinePopupRef = React.useRef(null);

  const popupSettings = {
    ...(inlinePopup && inlinePopupRef ? { popupSettings: { appendTo: inlinePopupRef.current } } : {}),
  };

  const editorRef = React.useRef(null);
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : '';
  const errorId = showValidationMessage ? `${id}_error` : '';
  const labelId = label ? `${id}_label` : '';

  return (
    <FieldWrapper style={wrapperStyle}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={value ? value.toString() : null}
        {...(readOnly ? { className: 'label-readonly' } : {})}
      >
        <BaseDropDownTree
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          dataItemKey={others.dataItemKey}
          textField={others.textField}
          readOnly={readOnly}
          optional={optional}
          dataTestId={editorId}
          {...popupSettings}
          {...others}
        />
      </FloatingLabel>
      {inlinePopup && <div ref={inlinePopupRef} />}
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormDropDownTree;
